import { isBefore } from 'date-fns';
import { useState } from 'react';
import {
    usePostCreateCompletedServiceOrderMutation,
    usePostCreateStartedServiceOrderMutation,
} from '../../../api/serviceOptionsApi';
import { trimmedOrUndefined } from '../../../api/utils';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { Service, ServiceOption } from '../serviceOption.types';

export type ServiceOptionCreateOrderDetails = {
    startedAt: Date | undefined;
    completedAt: Date | undefined;
    generalInformation: string | undefined;
};

export type SidebarFormFieldError<T> = { type: T };
export type ExecutionDateErrorType = 'dateInFuture' | 'endDateBeforeStartDateError' | 'oneMandatory';

export type ServiceOptionCreateOrderErrors = {
    startedAt?: SidebarFormFieldError<ExecutionDateErrorType>;
    completedAt?: SidebarFormFieldError<ExecutionDateErrorType>;
};

export type ServiceOptionDateField = 'startedAt' | 'completedAt';

type DateParseErrors = Record<ServiceOptionDateField, boolean>;

const initialFormData: ServiceOptionCreateOrderDetails = {
    startedAt: undefined,
    completedAt: undefined,
    generalInformation: undefined,
};

const initialDateParseErrors: DateParseErrors = {
    startedAt: false,
    completedAt: false,
};

export const useServiceOptionCreateOrderForm = (selectedServiceOption: ServiceOption | undefined, service: Service) => {
    const { createStartedServiceOrder, createCompletedServiceOrder, isCreationLoading } =
        useServiceOrderCreationMutations();
    const [formErrors, setFormErrors] = useState<ServiceOptionCreateOrderErrors>({});
    const [formData, setFormData] = useState<ServiceOptionCreateOrderDetails>(initialFormData);
    const [dateParseErrors, setDateParseErrors] = useState<DateParseErrors>(initialDateParseErrors);

    const setDateParseError = (field: ServiceOptionDateField, hasParseError: boolean) => {
        setDateParseErrors((current) => ({ ...current, [field]: hasParseError }));
    };

    const validateForm = (): boolean => {
        if (selectedServiceOption === undefined) {
            return false;
        }

        const newFormErrors = validateFormData(formData);
        setFormErrors(newFormErrors);
        const hasParseError = Object.values(dateParseErrors).some(Boolean);
        const hasBusinessError = Object.values(newFormErrors).some((error) => error !== undefined);
        return !hasParseError && !hasBusinessError;
    };

    const createServiceOrderFromOptionForm = () => {
        const serviceOptionId = selectedServiceOption!.id;
        if (formData.completedAt === undefined) {
            return createStartedServiceOrder({
                serviceOptionId,
                startInfo: {
                    service,
                    startedAt: formData.startedAt!,
                    generalInformation: trimmedOrUndefined(formData.generalInformation),
                },
            });
        }

        return createCompletedServiceOrder({
            serviceOptionId,
            completion: {
                service,
                startedAt: formData.startedAt,
                completedAt: formData.completedAt,
                generalInformation: trimmedOrUndefined(formData.generalInformation),
            },
        });
    };

    const handleCreateServiceOrder = async () => {
        if (validateForm() && selectedServiceOption !== undefined) {
            try {
                await createServiceOrderFromOptionForm().unwrap();
                showSuccessNotification('outboundOrderBook.notification.serviceOrder.createdFromServiceOption.success');
                setFormData(initialFormData);
                setFormErrors({});
                setDateParseErrors(initialDateParseErrors);
            } catch (error) {
                handleQueryError(error);
            }
        }
    };

    return {
        formData,
        setFormData,
        formErrors,
        setDateParseError,
        hasDateParseError: Object.values(dateParseErrors).some(Boolean),
        handleCreateServiceOrder,
        isCreationLoading,
    };
};

const useServiceOrderCreationMutations = () => {
    const [createStartedServiceOrder, { isLoading: isStartCreationLoading }] =
        usePostCreateStartedServiceOrderMutation();
    const [createCompletedServiceOrder, { isLoading: isCompletionCreationLoading }] =
        usePostCreateCompletedServiceOrderMutation();

    return {
        createStartedServiceOrder,
        createCompletedServiceOrder,
        isCreationLoading: isStartCreationLoading || isCompletionCreationLoading,
    };
};

const validateFormData = ({
    startedAt,
    completedAt,
}: ServiceOptionCreateOrderDetails): ServiceOptionCreateOrderErrors => {
    if (startedAt === undefined && completedAt === undefined) {
        return {
            completedAt: { type: 'oneMandatory' },
        };
    }

    if (completedAt === undefined) {
        return {};
    }

    if (completedAt > new Date()) {
        return {
            completedAt: { type: 'dateInFuture' },
        };
    }

    if (startedAt !== undefined && isBefore(completedAt, startedAt)) {
        return {
            completedAt: { type: 'endDateBeforeStartDateError' },
        };
    }

    return {};
};
